import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { StarRating, StarDisplay } from "../components/StarRating";
import { RatingCard } from "../components/RatingCard";
import { LevelBadge, BadgeList } from "../components/LevelBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { 
  ShieldCheck, 
  Star,
  Camera,
  X,
  Upload,
  AlertTriangle,
  Shield
} from "lucide-react";

const categories = [
  { id: "bienveillance", label: "Bienveillance" },
  { id: "aide", label: "Aide" },
  { id: "comportement", label: "Comportement" },
  { id: "ponctualite", label: "Ponctualité" },
  { id: "honnetete", label: "Honnêteté" },
  { id: "respect", label: "Respect" },
];

const UserProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser, token } = useAuth();
  const [user, setUser] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [ratingForm, setRatingForm] = useState({
    stars: 0,
    category: "",
    comment: ""
  });
  const [proofFiles, setProofFiles] = useState([]);
  const fileInputRef = useRef(null);

  const isOwnProfile = currentUser?.id === userId;
  const canRate = currentUser?.can_rate;
  const hasWarning = user?.badges?.includes("warning");

  const fetchData = useCallback(async () => {
    try {
      const [userRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/ratings/user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUser(userRes.data);
      setRatings(ratingsRes.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("Utilisateur non trouvé");
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    if (token && userId) {
      fetchData();
    }
  }, [token, userId, fetchData]);

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPhotoUrl = (path) => {
    if (path && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (proofFiles.length + files.length > 5) {
      toast.error("Maximum 5 fichiers autorisés");
      return;
    }
    setProofFiles([...proofFiles, ...files]);
  };

  const removeFile = (index) => {
    setProofFiles(proofFiles.filter((_, i) => i !== index));
  };

  const handleSubmitRating = async () => {
    if (ratingForm.stars === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }
    if (!ratingForm.category) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    setSubmitting(true);

    try {
      const ratingRes = await axios.post(
        `${API}/ratings`,
        {
          rated_user_id: userId,
          stars: ratingForm.stars,
          category: ratingForm.category,
          comment: ratingForm.comment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const ratingId = ratingRes.data.id;

      for (const file of proofFiles) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/upload/proof/${ratingId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      }

      const [userRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/ratings/user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUser(userRes.data);
      setRatings(ratingsRes.data);

      setRatingForm({ stars: 0, category: "", comment: "" });
      setProofFiles([]);
      setDialogOpen(false);
      toast.success("Évaluation envoyée avec succès !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto space-y-6">
          <Skeleton className="h-48 w-full rounded-3xl" />
          <Skeleton className="h-32 w-full rounded-3xl" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Utilisateur non trouvé</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Warning Banner */}
        {hasWarning && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3 animate-fadeIn">
            <AlertTriangle size={24} className="text-red-500 flex-shrink-0" />
            <div>
              <p className="font-bold text-red-700">Prudence recommandée</p>
              <p className="text-sm text-red-600">Ce profil a reçu plusieurs signalements</p>
            </div>
          </div>
        )}

        {/* Profile Header */}
        <Card className={`guardian-card animate-fadeIn ${hasWarning ? 'border-red-200' : ''}`} data-testid="user-profile-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <Avatar className="h-28 w-28 border-4 border-white shadow-lg">
                <AvatarImage src={getPhotoUrl(user.profile_photo)} alt={user.full_name} />
                <AvatarFallback className="bg-[#FF3366] text-white text-3xl font-bold">
                  {getInitials(user.full_name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    {user.full_name}
                  </h1>
                  {user.is_verified && (
                    <ShieldCheck size={24} className="text-green-500" />
                  )}
                </div>
                
                <div className="flex items-center justify-center sm:justify-start gap-3 mb-3">
                  <LevelBadge level={user.level} size="default" />
                  <StarDisplay rating={user.average_rating} size={18} />
                  <span className="text-slate-500 text-sm">
                    ({user.total_ratings} avis)
                  </span>
                </div>

                {user.badges && user.badges.length > 0 && (
                  <BadgeList badges={user.badges} size="default" />
                )}

                {user.bio && (
                  <p className="text-slate-600 mt-3">{user.bio}</p>
                )}
              </div>

              {!isOwnProfile && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-guardian rounded-full" 
                      disabled={!canRate}
                      data-testid="rate-user-btn"
                    >
                      <Star size={18} className="mr-2" />
                      Évaluer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md rounded-3xl">
                    <DialogHeader>
                      <DialogTitle className="font-extrabold text-xl" style={{ fontFamily: 'Outfit' }}>
                        Évaluer {user.full_name}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                      <div className="space-y-2">
                        <Label className="font-bold">Note</Label>
                        <div className="flex justify-center py-2">
                          <StarRating
                            rating={ratingForm.stars}
                            interactive
                            size={40}
                            onRatingChange={(value) => setRatingForm({ ...ratingForm, stars: value })}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold">Catégorie</Label>
                        <Select
                          value={ratingForm.category}
                          onValueChange={(value) => setRatingForm({ ...ratingForm, category: value })}
                        >
                          <SelectTrigger className="rounded-xl" data-testid="category-select">
                            <SelectValue placeholder="Sélectionner une catégorie" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold">Commentaire (optionnel)</Label>
                        <Textarea
                          placeholder="Décrivez votre expérience..."
                          value={ratingForm.comment}
                          onChange={(e) => setRatingForm({ ...ratingForm, comment: e.target.value })}
                          rows={3}
                          className="rounded-xl resize-none"
                          data-testid="rating-comment-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold">Preuves (optionnel)</Label>
                        <div 
                          className="dropzone"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload size={28} className="mx-auto mb-2 text-slate-400" />
                          <p className="text-sm text-slate-500">
                            Ajouter des photos ou vidéos
                          </p>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          className="hidden"
                          onChange={handleFileSelect}
                          data-testid="proof-file-input"
                        />

                        {proofFiles.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {proofFiles.map((file, index) => (
                              <div key={`${file.name}-${file.size}-${index}`} className="relative group">
                                <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                                  {file.type.startsWith("image/") ? (
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <Camera size={24} className="text-slate-400" />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        className="rounded-full"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSubmitRating}
                        disabled={submitting}
                        className="btn-guardian rounded-full"
                        data-testid="submit-rating-btn"
                      >
                        {submitting ? "Envoi..." : "Envoyer"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              
              {!isOwnProfile && !canRate && (
                <div className="text-center">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Shield size={14} />
                    Devenez Gardien pour évaluer
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ratings List */}
        <Card className="guardian-card animate-fadeIn stagger-1" data-testid="user-ratings">
          <CardHeader>
            <CardTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
              Évaluations reçues ({ratings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ratings.length > 0 ? (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <RatingCard 
                    key={rating.id} 
                    rating={rating} 
                    showRatedUser={false} 
                    showRater={true} 
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <Star size={40} className="empty-state-icon" />
                <p className="font-medium">Aucune évaluation</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default UserProfilePage;
